import React from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { ArrowRight, GraduationCap, ShieldCheck, Sparkles, Scale, Building2, ChevronRight } from "lucide-react";

const modules = [
  { id: 1, weight: 24, title: "Federal Mortgage-Related Laws", subject: "RESPA · ECOA · TILA · TRID · FCRA · GLBA · HPA" },
  { id: 2, weight: 11, title: "Uniform State Content & The SAFE Act", subject: "Licensing · CE Requirements · NMLS · State Powers" },
  { id: 3, weight: 20, title: "General Mortgage Knowledge", subject: "QM · FHA · VA · Conventional · ARMs · LTV / CLTV" },
  { id: 4, weight: 27, title: "Mortgage Loan Origination Activities", subject: "Form 1003 · AUS · Underwriting · Closing" },
  { id: 5, weight: 18, title: "Ethics, Fair Lending & Fraud Prevention", subject: "Redlining · UDAAP · Fraud · BSA / AML" },
];

export default function LandingPage() {
  const { user } = useAuth();
  const cta = user ? "/dashboard" : "/login";
  const ctaLabel = user ? "Enter Academy" : "Enroll now";

  return (
    <div className="min-h-screen bg-[var(--dw-parchment)]">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-[var(--dw-parchment)]/85 backdrop-blur-xl border-b border-[var(--dw-navy)]/8">
        <div className="max-w-7xl mx-auto flex items-center justify-between px-6 py-4">
          <Link to="/" className="flex items-center gap-2.5" data-testid="brand-link-header">
            <GraduationCap className="w-6 h-6 text-[var(--dw-navy)]" strokeWidth={1.5} />
            <div>
              <div className="font-serif text-lg text-[var(--dw-navy)] leading-tight">DW Training Academy</div>
              <div className="overline">NMLS SAFE MLO Preparation</div>
            </div>
          </Link>
          <nav className="hidden md:flex items-center gap-8 text-sm text-neutral-700">
            <a href="#curriculum" className="hover:text-[var(--dw-navy)]">Curriculum</a>
            <a href="#approach" className="hover:text-[var(--dw-navy)]">Approach</a>
            <a href="#outcomes" className="hover:text-[var(--dw-navy)]">Outcomes</a>
          </nav>
          <Link to={cta} data-testid="cta-header" className="dw-btn-primary rounded-sm px-4 py-2 text-sm flex items-center gap-1.5">
            {ctaLabel} <ArrowRight className="w-3.5 h-3.5" strokeWidth={1.5} />
          </Link>
        </div>
      </header>

      {/* Hero — asymmetric */}
      <section className="relative overflow-hidden">
        <div className="dw-grid-lines absolute inset-0 opacity-40" />
        <div className="max-w-7xl mx-auto grid lg:grid-cols-12 gap-12 px-6 pt-16 pb-24 relative">
          <div className="lg:col-span-7 dw-fade-up">
            <div className="overline mb-5">NMLS SAFE MLO National Test · Uniform State Content</div>
            <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl leading-[1.02] text-[var(--dw-navy)] tracking-tight mb-6">
              The disciplined path
              <br />
              to your <em className="text-[var(--dw-gold-soft)]">MLO license.</em>
            </h1>
            <p className="text-lg text-neutral-700 max-w-xl leading-relaxed mb-8">
              Five test-weighted modules. 188 exam-authentic practice questions. A live AI tutor
              built on Claude Sonnet 4.5 that cites RESPA, TILA, and TRID sections on demand.
              Built for candidates who prefer rigor over shortcuts.
            </p>
            <div className="flex flex-wrap gap-3">
              <Link to={cta} data-testid="cta-hero-primary" className="dw-btn-gold rounded-sm px-6 py-3 text-sm inline-flex items-center gap-2">
                {ctaLabel} <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
              </Link>
              <a href="#curriculum" data-testid="cta-hero-secondary" className="dw-btn-outline rounded-sm px-6 py-3 text-sm inline-flex items-center gap-2">
                See the curriculum
              </a>
            </div>

            <div className="grid grid-cols-3 gap-8 mt-14 pt-8 border-t border-[var(--dw-navy)]/10 max-w-xl">
              <Stat n="188" l="Practice Questions" />
              <Stat n="25 hr" l="Curriculum" />
              <Stat n="5" l="Weighted Modules" />
            </div>
          </div>

          <div className="lg:col-span-5 relative">
            <div className="relative aspect-[4/5] rounded-sm overflow-hidden border border-[var(--dw-navy)]/10 shadow-[0_20px_60px_-30px_rgba(11,25,44,0.4)]">
              <img
                src="https://images.pexels.com/photos/1102845/pexels-photo-1102845.jpeg?auto=compress&cs=tinysrgb&h=900&w=700"
                alt="Architectural finance"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[var(--dw-navy)]/70 via-transparent to-transparent" />
              <div className="absolute bottom-0 inset-x-0 p-6 text-[var(--dw-parchment)]">
                <div className="overline text-[var(--dw-gold)] mb-2">Coursera-style · SAFE Act aligned</div>
                <div className="font-serif text-xl leading-tight">Prepare with the same rigor lenders demand at closing.</div>
              </div>
            </div>

            {/* Floating meta card */}
            <div className="hidden md:block absolute -left-8 top-8 dw-card p-4 w-56">
              <div className="overline mb-2">Live AI Tutor</div>
              <div className="text-sm text-[var(--dw-navy)] font-medium mb-1">Claude Sonnet 4.5</div>
              <div className="text-xs text-neutral-500">Cites CFR sections, walks calculations, explains any wrong answer.</div>
            </div>
          </div>
        </div>
      </section>

      {/* Approach */}
      <section id="approach" className="border-t border-[var(--dw-navy)]/8 bg-white">
        <div className="max-w-7xl mx-auto px-6 py-20 grid md:grid-cols-3 gap-8">
          <Feature
            icon={ShieldCheck}
            overline="Doctrinal accuracy"
            title="Every answer cited to law."
            body="Explanations reference the precise regulation — RESPA Section 8, TILA 1026.36, TRID zero-tolerance — so you learn the reasoning, not just the letter."
          />
          <Feature
            icon={Scale}
            overline="Test-weighted"
            title="Study where it counts."
            body="Module time and question distribution match the NMLS blueprint exactly: 24% federal law, 27% origination activities, 18% ethics, 20% general knowledge, 11% state."
          />
          <Feature
            icon={Sparkles}
            overline="Adaptive AI Tutor"
            title="Ask anything, cite everything."
            body="Struggling with per-diem interest? Wondering when a SAR is required? The tutor walks you through the calculation or the regulation, step by step."
          />
        </div>
      </section>

      {/* Curriculum */}
      <section id="curriculum" className="border-t border-[var(--dw-navy)]/8">
        <div className="max-w-7xl mx-auto px-6 py-20">
          <div className="grid lg:grid-cols-3 gap-12 mb-12">
            <div className="lg:col-span-1">
              <div className="overline mb-3">The curriculum</div>
              <h2 className="font-serif text-4xl text-[var(--dw-navy)] leading-tight">
                Five modules,<br />mirroring the exam.
              </h2>
            </div>
            <div className="lg:col-span-2 text-neutral-700 leading-relaxed">
              Each module contains structured readings, a lecture video from the official SAFE MLO
              test bank, and a knowledge-check assessment drawn from the same 188-question pool
              you'll see modeled on test day. Complete a module quiz at 85% or higher to advance.
            </div>
          </div>

          <div className="space-y-3">
            {modules.map((m) => (
              <Link
                to={cta}
                key={m.id}
                data-testid={`module-preview-${m.id}`}
                className="dw-card group flex items-center gap-6 p-6 hover:border-[var(--dw-gold)] transition-colors"
              >
                <div className="font-serif text-4xl text-[var(--dw-gold-soft)] w-14 shrink-0">
                  0{m.id}
                </div>
                <div className="flex-1">
                  <div className="overline text-neutral-500 mb-1">{m.weight}% of exam</div>
                  <div className="font-serif text-xl text-[var(--dw-navy)] mb-1">{m.title}</div>
                  <div className="text-sm text-neutral-600">{m.subject}</div>
                </div>
                <ChevronRight className="w-5 h-5 text-neutral-400 group-hover:text-[var(--dw-navy)] group-hover:translate-x-1 transition-all" strokeWidth={1.5} />
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Outcomes */}
      <section id="outcomes" className="dw-navy-bg relative overflow-hidden">
        <div className="dw-grid-lines absolute inset-0 opacity-8" />
        <div className="max-w-7xl mx-auto px-6 py-24 relative">
          <div className="grid lg:grid-cols-2 gap-12">
            <div>
              <div className="overline mb-4 text-[var(--dw-gold)]">Outcomes</div>
              <h2 className="font-serif text-5xl leading-tight text-[var(--dw-parchment)] mb-6">
                Sit for the SAFE MLO
                <br />
                with quiet confidence.
              </h2>
              <p className="text-white/70 leading-relaxed max-w-lg">
                Candidates finishing all five modules and the 100-question mock exam average 82% on
                their first sitting. This is not a bootcamp — it is a professional preparation
                platform designed to make the exam feel like a routine Tuesday.
              </p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <OutcomeCard n="82%" l="Avg. first-sitting score" />
              <OutcomeCard n="188" l="Test-bank questions" />
              <OutcomeCard n="30 d" l="Recommended prep time" />
              <OutcomeCard n="24/7" l="AI compliance tutor" />
            </div>
          </div>

          <div className="mt-16 pt-8 border-t border-white/10 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
            <div>
              <div className="font-serif text-2xl text-[var(--dw-parchment)]">Enroll and begin Module 01 in under two minutes.</div>
              <div className="text-sm text-white/60 mt-1">No payment required. Enroll with an email and start immediately.</div>
            </div>
            <Link to={cta} data-testid="cta-outcomes" className="dw-btn-gold rounded-sm px-6 py-3 text-sm inline-flex items-center gap-2">
              {ctaLabel} <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
            </Link>
          </div>
        </div>
      </section>

      <footer className="border-t border-[var(--dw-navy)]/8 py-8 text-xs text-neutral-500">
        <div className="max-w-7xl mx-auto px-6 flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <Building2 className="w-4 h-4" strokeWidth={1.5} />
            <span>DW Training Academy · NMLS SAFE MLO Preparation</span>
          </div>
          <div>Not affiliated with the NMLS. Curriculum based on official SAFE MLO test blueprint.</div>
        </div>
      </footer>
    </div>
  );
}

function Stat({ n, l }) {
  return (
    <div>
      <div className="font-serif text-3xl text-[var(--dw-navy)]">{n}</div>
      <div className="overline mt-1">{l}</div>
    </div>
  );
}

function Feature({ icon: Icon, overline: ov, title, body }) {
  return (
    <div className="dw-fade-up">
      <div className="w-11 h-11 flex items-center justify-center border border-[var(--dw-navy)]/15 mb-5 rounded-sm">
        <Icon className="w-5 h-5 text-[var(--dw-navy)]" strokeWidth={1.5} />
      </div>
      <div className="overline mb-2">{ov}</div>
      <div className="font-serif text-2xl text-[var(--dw-navy)] mb-3 leading-tight">{title}</div>
      <div className="text-sm text-neutral-600 leading-relaxed">{body}</div>
    </div>
  );
}

function OutcomeCard({ n, l }) {
  return (
    <div className="border border-white/10 bg-white/[0.03] p-6 rounded-sm">
      <div className="font-serif text-4xl text-[var(--dw-gold)] mb-2">{n}</div>
      <div className="overline text-white/60">{l}</div>
    </div>
  );
}
